import {ChangeDetectorRef, computed, Directive, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NgControl} from '@angular/forms';
import {Subscription} from 'rxjs';

@Directive()
export abstract class BaseAppControlValueAccessorComponent
    implements ControlValueAccessor, OnInit, OnDestroy {

    // Injeções
    protected ngControl = inject(NgControl, {optional: true});
    private cdr = inject(ChangeDetectorRef);

    // Estado com signals
    private _isRequired = signal<boolean>(false);
    private _isDisabled = signal<boolean>(false);
    private _value = signal<any>(null);

    // Computed properties públicas
    isRequired = computed(() => this._isRequired());
    isDisabled = computed(() => this._isDisabled());

    // Subscriptions
    private controlSubscription?: Subscription;

    // Callbacks do ControlValueAccessor
    protected onChange: (value: any) => void = () => {
    };
    protected onTouched: () => void = () => {
    };

    protected constructor() {
        // Configura o valueAccessor se ngControl estiver disponível
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        this.setupControlListener();
    }

    ngOnDestroy(): void {
        this.cleanupSubscriptions();
    }

    // ===== MÉTODOS DO CONTROLVALUEACCESSOR =====

    writeValue(value: any): void {
        this._value.set(value);
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._isDisabled.set(isDisabled);
        this.cdr.markForCheck();
    }

    // ===== MÉTODOS PROTEGIDOS PARA CLASSES FILHAS =====

    protected setValue(value: any): void {
        this._value.set(value);
        this.onChange(value);
    }

    protected getValue(): any {
        return this._value();
    }

    protected markAsTouched(): void {
        this.onTouched();
    }

    protected getControl(): AbstractControl | null {
        return this.ngControl?.control || null;
    }

    protected setupControlListener(): void {
        if (!this.ngControl?.control) return;

        const control = this.ngControl.control;

        this.controlSubscription = control.statusChanges.subscribe(() => {
            this.checkIfRequired();
        });

        this.checkIfRequired();
        this.checkIfDisabled();
    }

    // ===== MÉTODOS PRIVADOS =====

    private checkIfRequired(): void {
        if (!this.ngControl?.control) return;

        const control = this.ngControl.control;
        const wasRequired = this._isRequired();

        const hasRequiredValidator = this.hasRequiredValidator(control);

        if (hasRequiredValidator !== wasRequired) {
            this._isRequired.set(hasRequiredValidator);
        }
    }

    private checkIfDisabled(): void {
        if (!this.ngControl?.control) return;

        const control = this.ngControl.control;
        this._isDisabled.set(control.disabled);
    }

    private hasRequiredValidator(control: AbstractControl): boolean {
        if (!control.validator) return false;

        const validationResult = control.validator({value: ''} as AbstractControl);
        return validationResult?.['required'] === true;
    }

    private cleanupSubscriptions(): void {
        if (this.controlSubscription) {
            this.controlSubscription.unsubscribe();
            this.controlSubscription = undefined;
        }
    }

    // ===== MÉTODOS UTILITÁRIOS PARA CLASSES FILHAS =====

    protected get hasErrors(): boolean {
        const control = this.getControl();
        return !!(control?.invalid && control?.touched);
    }

    protected get isDirty(): boolean {
        return this.getControl()?.dirty ?? false;
    }

    protected get isTouched(): boolean {
        return this.getControl()?.touched ?? false;
    }

    protected get isValid(): boolean {
        return this.getControl()?.valid ?? true;
    }

    protected get isInvalid(): boolean {
        return this.getControl()?.invalid ?? false;
    }

    protected getErrorMessages(): string[] {
        const control = this.getControl();
        if (!control?.errors) return [];

        return Object.keys(control.errors).map(key => {
            const error = control.errors![key];
            return typeof error === 'string' ? error : key;
        });
    }

    protected replaceTablerIconPrefix(classes: string): string {
        return classes.replace('ti-', '');
    }

    protected inputId = `app-input-${Math.random().toString(36).substr(2, 9)}`;
}
